<template>
	<div class="reset-password">
		<SliderLayout
			ref="layout"
			header-display-icon
			:min-height="minHeight()"
		>
			<div>
				<div class="reset-password__content">
					<div class="reset-password__error">
						<helio-message
							:active="error.description.length > 0 && error.title.length > 0"
							icon-token="exclamation-circle"
							icon-weight="bold"
							state="danger"
							:title="error.title"
							@close="() => {error.title = ''; error.description = ''}"
						>
							<template v-slot:content>
								<p>
									{{ error.description }}
								</p>
							</template>
						</helio-message>
					</div>
					<div class="reset-password__desc">
						<div class="reset-password__title">
							{{ translation['new-password'] }}
						</div>
						<div class="reset-password__desc">
							{{ translation['description'] }}
						</div>
					</div>
					<div class="reset-password__input">
						<div class="reset-password__text">
							{{ translation['new-password'] }}
						</div>
						<helio-field
							data-cy="new-password-input"
							:display-success="false"
							:initial-value="initialPassword"
							:initial-errors="specialCharacterErrors"
							:autofocus="true"
							autocomplete="off"
							type="password"
							@changed="password = $event"
							@blurred="validateSpecialCharacter"
							@keyup.enter.native="(!isButtonDisabled)?resetPassword():()=>{}"
						/>
						<Validation
							class="reset-password__validation"
							:password="password"
							@validation="rulesRespected = $event"
							@special-character="specialCharacter = $event"
						/>
						<div class="reset-password__text">
							{{ translation['confirm-password'] }}
						</div>
						<helio-field
							data-cy="new-password-confirm"
							:display-success="false"
							:initial-errors="errors"
							:initial-value="initialPassword"
							autocomplete="off"
							type="password"
							@keyup.enter.native="(!isButtonDisabled)?resetPassword():()=>{}"
							@changed="repeatedPassword = $event"
						/>
						<helio-button
							:disabled="isButtonDisabled"
							class="reset-password__button"
							data-cy="reset-password-button"
							:state="buttonState"
							color="primary"
							:text="translation['save']"
							size="large"
							@click="resetPassword()"
						/>
					</div>
					<router-link
						class="reset-password__back-to-login"
						data-cy="back-to-login"
						:to="{name: `login-${$i18n.locale}`}"
					>
						{{ translation['back-to-login'] }}
					</router-link>
				</div>
			</div>
		</SliderLayout>
		<ResetAnimation v-if="animationEnabled" />
	</div>
</template>

<script>
import SliderLayout from '@/views/SliderLayout';
import Validation from '@/components/Validation';
import ResetAnimation from './ResetPasswordAnimation';

import { translationMixin, titleMixin } from '@/mixins';

const ERROR_CODE_INVALID_TOKEN = 401;
const ERROR_CODE_INVALID_PASSWORD = 422;
const ANIMATION_DURATION = 3000;

export default {
	name: 'ResetPassword',
	components: {
		SliderLayout,
		Validation,
		ResetAnimation,
	},
	mixins: [translationMixin, titleMixin],
	data() {
		return {
			rulesRespected: false,
			specialCharacter: false,
			animationEnabled: false,
			password: '',
			repeatedPassword: '',
			resetToken: null,
			email: null,
			error: null,
			enableResetPassword: true,
			initialPassword: '',
			specialCharacterErrors: [],
		};
	},
	computed: {
		translation() {
			return this.currentTranslation['reset-password'];
		},

		errors() {
			if (this.repeatedPassword.length > 0 && this.password !== this.repeatedPassword) {
				return [this.translation['passwords-do-not-match']];
			}

			return [];
		},

		isButtonDisabled() {
			return !this.rulesRespected || this.password !== this.repeatedPassword;
		},

		buttonState() {
			if (this.error.title.length > 1) {
				return 'is-unsuccessful';
			}
			if (this.animationEnabled) {
				return 'is-successful';
			}
			if (!this.enableResetPassword) {
				return 'is-loading';
			}
			return null;
		},

		isErrorPresent() {
			return this.error.description.length > 0 && this.error.title.length > 0;
		},
	},
	watch: {
		password() {
			this.specialCharacterErrors = [];
		},
	},
	created() {
		this.resetToken = this.$store.getters['application/getResetToken'];
		this.email = this.$store.getters['application/getEmail'];
		this.error = { title: '', description: '' };

		// Only display this page if the reset token is valid.
		if (this.resetToken == null || this.email == null) {
			this.$router.push({ name: `login-${this.$i18n.locale}` });
		}
	},
	beforeDestroy() {
		this.$refs.layout.$refs.header.$el.querySelector('a')
			.removeEventListener('click', this.onRequestTranslation);
	},

	mounted() {
		this.$refs.layout.$refs.header.$el.querySelector('a')
			.addEventListener('click', this.onRequestTranslation);

		// We want to create a changed on the initial value in order
		// to be sure to clear what LastPass and other services
		// could have inject as password without our consent
		setTimeout(() => {
			this.initialPassword = ' ';

			this.$nextTick(() => {
				this.initialPassword = '';
			});
		}, 1000);
	},
	methods: {
		validateSpecialCharacter() {
			setTimeout(() => {
				if (!this.specialCharacter && (this.password.includes('+') || this.password.includes('='))) {
					this.specialCharacterErrors = [this.translation['x-and-equal-not-special']];
				} else {
					this.specialCharacterErrors = [];
				}
			}, 50);
		},

		minHeight() {
			return this.isErrorPresent ? 880 : 784;
		},

		onRequestTranslation(e) {
			const resetInfo = this.$store.getters['application/getResetInfo'];
			if (
				resetInfo != null
				&& resetInfo.code != null
				&& resetInfo.email != null
				&& resetInfo.lang != null
			) {
				const lang = (resetInfo.lang === 'fr') ? 'en' : 'fr';
				const endpoint = `${window.location.origin}/password/reset-code`;
				const params = `code=${resetInfo.code}&email=${resetInfo.email}&lang=${lang}`;
				window.location = `${endpoint}?${params}`;
				e.preventDefault();
			}
		},

		resetPassword() {
			this.enableResetPassword = false;
			this.$store.dispatch('sso/RESET_PASSWORD', {
				email: this.email,
				newPassword: this.password,
				resetToken: this.resetToken,
			})
				.then((response) => {
					const { code } = response.data;
					if (code === 200) {
						this.animationEnabled = true;
						this.$store.dispatch('sso/LOGOUT')
							.then(() => this.$store.dispatch('sso/LOGIN', {
								email: this.email,
								password: this.password,
							}))
							.then(res => this.$store.dispatch('sso/SAVE_TOKENS', res.data.data))
							.then(() => {
								setTimeout(() => {
									let redirectDomain = this.$store.getters['application/getRedirect'];
									if (redirectDomain == null) {
										redirectDomain = `${process.env.VUE_APP_CENTIVA_PROTOCOL}://${process.env.VUE_APP_CENTIVA_DOMAIN}`;
									}
									window.location = redirectDomain;
								}, ANIMATION_DURATION);
							})
							.catch((error) => {
								throw error;
							});
					} else {
						this.enableResetPassword = true;
						throw new Error(code);
					}
				})
				.catch((error) => {
					this.enableResetPassword = true;
					try {
						const { code } = error.response.data;
						switch (code) {
							case ERROR_CODE_INVALID_TOKEN:
								// eslint-disable-next-line no-case-declarations
								const forgotPasswordError = {
									title: this.translation['title-invalid-link'],
									description: this.translation['invalid-link'],
								};
								this.$store.dispatch('application/SET_PROPERTY', { forgotPasswordError });
								this.$router.push({ name: `forgot-password-${this.$i18n.locale}` });
								break;
							case ERROR_CODE_INVALID_PASSWORD:
								this.error.title = this.translation['title-invalid-password'];
								this.error.description = this.translation['invalid-password'];
								break;
							default:
								this.error.title = this.translation['title-generic-error'];
								this.error.description = this.translation['generic-error'];
								break;
						}
					} catch (e) {
						this.error.title = this.translation['title-generic-error'];
						this.error.description = this.translation['generic-error'];
					}
				});
		},
	},
};
</script>

<style lang="scss" src="./reset-password.scss" />
